import {
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { LANGUAGES } from '../../constants/languages';
import { combineLatest, map, switchMap } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

interface LanguageSelectOption {
  value: keyof typeof LANGUAGES;
  viewValue: string;
}

@Component({
  selector: 'resto-language-selector',
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule,
    MatOptionModule,
    MatButtonModule,
    MatIconModule,
  ],
  templateUrl: './language-selector.component.html',
  styleUrl: './language-selector.component.scss',
})
export class LanguageSelectorComponent {
  private translate = inject(TranslocoService);

  readonly prefix = input<boolean>(false);
  readonly placeholder = input<string>();
  readonly model = input.required<keyof typeof LANGUAGES>();
  readonly changed = output<void>();

  private languagesSource = signal<LanguageSelectOption[]>([
    { value: LANGUAGES.en, viewValue: 'English' },
    { value: LANGUAGES.de, viewValue: 'Deutsch' },
    { value: LANGUAGES.it, viewValue: 'Italiano' },
    { value: LANGUAGES.es, viewValue: 'Español' },
    { value: LANGUAGES.fr, viewValue: 'Français' },
  ]);

  private languageKeys = computed(() =>
    this.languagesSource().map((el) => `shared.languages.${el.value}`),
  );

  languages = toSignal(
    combineLatest([
      this.translate.langChanges$,
      toObservable(this.languageKeys),
    ]).pipe(
      switchMap(([_, languageKeys]) => {
        return this.translate.selectTranslate(languageKeys).pipe(
          map((el: string[]) => {
            const newLang: LanguageSelectOption[] = [];
            languageKeys.forEach((key: string, index: number) => {
              const splittedKeys = key.split('.');
              newLang.push({
                value: splittedKeys[2] as keyof typeof LANGUAGES,
                viewValue: el[index],
              });
            });
            return newLang;
          }),
        );
      }),
    ),
  );

  handleChange({ value }: { value: keyof typeof LANGUAGES }) {
    this.translate.setActiveLang(value);
  }
}
